import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React, {useEffect, useMemo, useState} from 'react';

import {isFilesEnabled} from 'auth/feature-enablement/FeatureStatus';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingConfig from 'BenchlingConfig';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ConnectionFilesTable from 'instruments/connection-files/ConnectionFilesTable';
import {ConnectionStatus, InstrumentServiceDetails} from 'instruments/connections/ConnectionConstants';
import {getInstrumentServiceDetails} from 'instruments/connections/ConnectionDAO';
import {
  FILE_STATUS_TO_TEXT,
  InstrumentData,
  InstrumentDataFileType,
  InstrumentFileStatus,
  isDirectRequestAdapter,
} from 'instruments/instrument-data/InstrumentDataConstants';
import * as InstrumentDataDAO from 'instruments/instrument-data/InstrumentDataDAO';
import InstrumentDataTable from 'instruments/instrument-data/InstrumentDataTable';
import {LoadingFileScreen} from 'nav/full-screen/LoadingFullScreen';
import notifications from 'notifications';
import {FilterKey} from 'search/SearchConstants';
import * as LoadingScreen from 'ui-library/LoadingScreen';

import {FileListingGraphQL} from '../../../../graphql-fragments/files/__generated/FileListingGraphQL';
import {ConnectionDataQuery, ConnectionDataQueryVariables} from './__generated/ConnectionDataQuery';

export interface ConnectionDataProps {
  connectionId: string;
  dataCatalogFolderId: string | null;
}

function ConnectionData(props: ConnectionDataProps): JSX.Element {
  const [instrumentData, setInstrumentData] = useState<Array<InstrumentData>>([]);
  const [files, setFiles] = useState<Array<FileListingGraphQL>>([]);
  const [instrumentServiceDetails, setInstrumentServiceDetails] = useState(
    null as InstrumentServiceDetails | null
  );

  useEffect((): void => {
    setInstrumentServiceDetails(null);
    if (props.connectionId) {
      getInstrumentServiceDetails(props.connectionId).then((instrumentServiceDetails) => {
        setInstrumentServiceDetails(instrumentServiceDetails);
      });
    }
  }, [props.connectionId]);

  const connectionIds = useMemo(() => [props.connectionId], [props.connectionId]);

  if (BenchlingConfig.ENABLE_FILE_BASED_CONNECT_RETRIEVE_DATA_MODAL) {
    return (
      <div className="react-tab-container">
        <ConnectionFilesTable
          connectionFiles={files}
          setConnectionFiles={setFiles}
          columns={['File name', 'Status', 'Modified at', 'Created at']}
          connectionIds={connectionIds}
          hideCheckboxColumn={true}
          onSelectRow={(rowIndex) => {
            const selectedFile = files[rowIndex];
            LoadingScreen.showFileLoading();
            FileBrowserActions.openFile(selectedFile.id);
          }}
        />
      </div>
    );
  } else {
    const columns = isDirectRequestAdapter(instrumentServiceDetails?.adapterId)
      ? ['File name', 'Status', 'Created at']
      : ['File name', 'Status', 'Modified at', 'Refetch'];
    if (BenchlingConfig.ENABLE_BENCHLING_CONNECT_DOWNLOAD_FILES_ZIP) {
      columns.push('Download Files');
    }
    const isConnectionAvailable = instrumentServiceDetails?.status.value === ConnectionStatus.AVAILABLE;

    return (
      <div className="react-tab-container">
        <InstrumentDataTable
          instrumentData={instrumentData}
          setInstrumentData={setInstrumentData}
          columns={columns}
          fileTypeFilter={InstrumentDataFileType.ORIGINAL}
          filterOptions={[]}
          initialFilterValues={{[FilterKey.CONNECTION]: [props.connectionId]}}
          hideCheckboxColumn={true}
          isConnectionAvailable={isConnectionAvailable}
          onSelectRow={
            isFilesEnabled()
              ? async (rowIndex) => {
                  const selectedInstrumentData = instrumentData[rowIndex];
                  const dataStatus = FILE_STATUS_TO_TEXT[selectedInstrumentData.status];
                  if (selectedInstrumentData.status !== InstrumentFileStatus.UPLOADED) {
                    notifications.error(`The file cannot be opened due to its '${dataStatus}' status.`);
                    return;
                  }
                  if (!props.dataCatalogFolderId) {
                    notifications.error(
                      'Files cannot be created in data catalog because a Benchling location has not been set for this connection.'
                    );
                    return;
                  }
                  LoadingScreen.showFileLoading();
                  const fileId = await InstrumentDataDAO.getOrCreateFile({
                    instrumentDataId: selectedInstrumentData.id,
                    folderId: props.dataCatalogFolderId,
                    connectionId: props.connectionId,
                  });
                  FileBrowserActions.openFile(fileId!);
                }
              : undefined
          }
        />
      </div>
    );
  }
}

export const CONNECTION_DATA_QUERY: DocumentNode = gql`
  query ConnectionDataQuery($connectionId: StrictString!) @skipAudit @engComponent(name: INSTRUMENTS) {
    connection(id: $connectionId) {
      ... on ConnectionNode {
        id
        dataCatalogFolder {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function QueryingConnectionData(props: {connectionId: string}): JSX.Element {
  const result = useBenchlingQuery<ConnectionDataQuery, ConnectionDataQueryVariables>(CONNECTION_DATA_QUERY, {
    variables: {connectionId: props.connectionId},
  });

  if (result.type !== QueryResultType.SUCCESS) {
    return <LoadingFileScreen />;
  }
  if (isInaccessible(result.data.connection)) {
    return (
      <WorkspaceItemInaccessibleGuard inaccessibleInfo={result.data.connection} modelType="connection" />
    );
  } else {
    return (
      <ConnectionData
        connectionId={props.connectionId}
        dataCatalogFolderId={result.data.connection.dataCatalogFolder?.id ?? null}
      />
    );
  }
}
